import { useCallback } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { getStoredGuestSessionToken, toGuestToken } from '@/utils/guestSession';

/**
 * Drop-in replacement for Clerk's `useAuth().getToken` — same `{ getToken }` shape,
 * so an existing call site becomes guest-aware by swapping only the hook. Returns a
 * redeemed guest's stored session token if there is one, marker-prefixed (see
 * `utils/guestSession.ts`) so API clients send it as `X-Assessment-Session-Token`
 * rather than `Authorization`; otherwise falls through to the Clerk token.
 *
 * A Clerk session always wins, and the guest token is read only once Clerk has
 * loaded and confirmed nobody is signed in. Both credentials can coexist in one
 * browser — an admin redeeming a test code is the obvious way — and preferring the
 * guest one would silently demote that admin for the next 24h.
 *
 * No refresh: the guest token is a flat 24h grant, so once it expires the next call
 * 401s and the caller (ProtectedRoute) treats the guest as logged out.
 */
export const useSessionToken = () => {
  const { isLoaded, isSignedIn, getToken: getClerkToken } = useAuth();

  const getToken = useCallback(async (): Promise<string | null> => {
    if (isLoaded && !isSignedIn) {
      const guestToken = getStoredGuestSessionToken();
      if (guestToken) return toGuestToken(guestToken);
    }
    return getClerkToken();
  }, [isLoaded, isSignedIn, getClerkToken]);

  return { getToken };
};

export default useSessionToken;
